import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowRightIcon,
  ArrowSquareOutIcon,
  ChatsCircleIcon,
  CheckCircleIcon,
  ClipboardTextIcon,
  HouseLineIcon,
  RobotIcon,
  PlugsIcon,
  PlayCircleIcon,
  StorefrontIcon,
  UploadSimpleIcon,
  UsersFourIcon,
  ToggleRightIcon,
} from '@phosphor-icons/react';
import { fetchSetupState, markStep, STEPS, STEP_META, type StepId } from '@/features/setup/api';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, StatusChip, cn } from '@smena/ui';
import { fetchAccount, patchAccount, patchPlaybook, patchSettings } from '@/features/account/api';
import { fetchProperties } from '@/features/properties/api';
import { PropertyOnboardingCard } from '@/features/properties/PropertyOnboardingCard';
import { DraftModeExplainer } from '@/features/autonomy/DraftModeExplainer';
import { SandboxChat } from '@/features/sandbox/SandboxChat';
import { MaxHistoryImportCard } from '@/features/max-personal/MaxHistoryImportCard';
import { TgHistoryImportCard } from '@/features/tg-personal/TgHistoryImportCard';
import {
  connectAvito,
  fetchAvitoOauthConfig,
  fetchAvitoStatus,
  importAvitoHistory,
  importAvitoItems,
  probeAvitoTariff,
  startAvitoOauth,
} from '@/features/avito/api';
import { GuestChannelChoice } from '@/features/channels/GuestChannelChoice';
import { BookingsImportCard } from '@/features/integrations/BookingsImportCard';
import { IcalFeedsCard } from '@/features/integrations/IcalFeedsCard';
import { RealtyCalendarCard } from '@/features/integrations/RealtyCalendarCard';

const ICONS: Record<StepId, React.ReactNode> = {
  account: <UsersFourIcon weight="bold" />,
  source: <PlugsIcon weight="bold" />,
  properties: <HouseLineIcon weight="bold" />,
  avito: <StorefrontIcon weight="bold" />,
  property_form: <ClipboardTextIcon weight="bold" />,
  chat_import: <UploadSimpleIcon weight="bold" />,
  channels: <ChatsCircleIcon weight="bold" />,
  persona: <RobotIcon weight="bold" />,
  demo_chat: <PlayCircleIcon weight="bold" />,
  switches: <ToggleRightIcon weight="bold" />,
};

export function SetupPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const stateQ = useQuery({ queryKey: ['setup'], queryFn: fetchSetupState });

  const currentStepIdx = Math.min(
    Math.max(Number(params.get('step') ?? stateQ.data?.currentStep ?? 1), 1),
    STEPS.length,
  );
  const currentStep = STEPS[currentStepIdx - 1]!;

  const mark = useMutation({
    mutationFn: (input: { id: StepId; action: 'completed' | 'skipped' }) => markStep(input.id, input.action),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['setup'] }),
  });

  const done = stateQ.data?.completed ?? [];
  const skipped = stateQ.data?.skipped ?? [];
  const finished = stateQ.data?.completedAt !== null && stateQ.data?.completedAt !== undefined;

  const totalMinutes = useMemo(() => {
    const remain = STEPS.filter((s) => !done.includes(s) && !skipped.includes(s));
    const total = remain.reduce((sum, id) => {
      const m = parseInt(STEP_META[id].est, 10);
      return sum + (Number.isFinite(m) ? m : 0);
    }, 0);
    return total;
  }, [done, skipped]);

  function goto(idx: number) {
    const p = new URLSearchParams(params);
    p.set('step', String(idx));
    setParams(p, { replace: true });
  }

  function next(action: 'completed' | 'skipped') {
    mark.mutate(
      { id: currentStep, action },
      {
        onSuccess: (s) => {
          if (s.completedAt) {
            toast.success('Настройка завершена');
            navigate('/attention');
          } else {
            goto(Math.min(currentStepIdx + 1, STEPS.length));
          }
        },
      },
    );
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-5">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-semibold">Настройка Smena</h1>
          <p className="text-sm text-muted">
            {finished
              ? 'Мастер пройден. Можно вернуться в панель.'
              : `Шаг ${currentStepIdx} из ${STEPS.length} · осталось ~${totalMinutes} мин`}
          </p>
        </div>
        {finished ? (
          <Button variant="secondary" onClick={() => navigate('/attention')}>
            В панель
          </Button>
        ) : null}
      </div>

      {/* Steps rail */}
      <Card>
        <CardContent className="flex flex-wrap gap-1 p-2">
          {STEPS.map((id, i) => {
            const idx = i + 1;
            const isDone = done.includes(id);
            const isSkipped = skipped.includes(id);
            const isCur = idx === currentStepIdx;
            return (
              <button
                key={id}
                type="button"
                onClick={() => goto(idx)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                  isCur && 'bg-accent/25 text-accent-fg',
                  !isCur && isDone && 'text-green',
                  !isCur && isSkipped && 'text-muted',
                  !isCur && !isDone && !isSkipped && 'text-muted hover:bg-panel2',
                )}
              >
                {isDone ? <CheckCircleIcon weight="fill" className="size-3.5" /> : ICONS[id]}
                <span>{idx}. {STEP_META[id].title}</span>
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Current step body */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="grid size-7 place-items-center rounded-md bg-accent/20 text-accent-fg">
              {ICONS[currentStep]}
            </span>
            {currentStepIdx}. {STEP_META[currentStep].title}
            <span className="ml-auto text-xs font-normal text-muted">
              ~{STEP_META[currentStep].est}
            </span>
          </CardTitle>
          <div className="text-xs text-muted">{STEP_META[currentStep].hint}</div>
        </CardHeader>
        <CardContent>
          <StepBody id={currentStep} />
        </CardContent>
      </Card>

      {/* Nav */}
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => goto(Math.max(1, currentStepIdx - 1))} disabled={currentStepIdx === 1}>
          ← Назад
        </Button>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => next('skipped')} disabled={mark.isPending}>
            Пропустить
          </Button>
          <Button
            onClick={() => next('completed')}
            disabled={mark.isPending || (currentStep === 'source' && !stateQ.data?.sourceReady)}
            title={
              currentStep === 'source' && !stateQ.data?.sourceReady
                ? 'Ждём первую бронь - из RealtyCalendar, iCal или файла.'
                : undefined
            }
          >
            {currentStepIdx === STEPS.length ? 'Завершить' : 'Далее'} <ArrowRightIcon weight="bold" />
          </Button>
        </div>
      </div>

      {/* Что вы пропустили - только на финальном шаге или после завершения */}
      {(currentStepIdx === STEPS.length || finished) && skipped.length > 0 ? (
        <Card className="border-amber/40 bg-amber/5">
          <CardHeader>
            <CardTitle>Что вы пропустили</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-1.5">
            {skipped.map((id) => (
              <div key={id} className="flex items-center justify-between text-sm">
                <span>
                  <StatusChip tone="amber">{STEP_META[id].title}</StatusChip>{' '}
                  <span className="text-muted">{STEP_META[id].hint}</span>
                </span>
                <button
                  type="button"
                  onClick={() => goto(STEPS.indexOf(id) + 1)}
                  className="text-accent-fg hover:underline"
                >
                  Вернуться
                </button>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

// ============================================================ StepBody

function StepBody({ id }: { id: StepId }) {
  if (id === 'account') return <StepAccount />;
  if (id === 'source') return <StepSource />;
  if (id === 'properties') return <StepProperties />;
  if (id === 'avito') return <StepAvito />;
  if (id === 'property_form') return <StepPropertyForm />;
  if (id === 'chat_import') return <StepChatImport />;
  if (id === 'channels') return <StepChannels />;
  if (id === 'persona') return <StepPersona />;
  if (id === 'demo_chat') return <StepDemoChat />;
  if (id === 'switches') return <StepSwitches />;
  return null;
}

function StepChannels() {
  // Выбор бота по каждому мессенджеру. Общий бот Smena включён по умолчанию и не требует
  // нажатий: раньше шаг писал «Ничего не подключено» и просил нажать «Использовать общий»,
  // хотя бот и так работал - отсутствие строки в channel_configs и означает «общий».
  const accQ = useQuery({ queryKey: ['account'], queryFn: fetchAccount });
  const whitelabel = Boolean(
    (accQ.data?.settings as { whitelabel?: { enabled?: boolean } } | undefined)?.whitelabel?.enabled,
  );
  return <GuestChannelChoice whitelabelEnabled={whitelabel} />;
}

function StepAccount() {
  const qc = useQueryClient();
  const accQ = useQuery({ queryKey: ['account'], queryFn: fetchAccount });
  const mut = useMutation({
    mutationFn: patchAccount,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['account'] });
      toast.success('Профиль сохранён');
    },
  });
  const [name, setName] = useState('');
  useEffect(() => {
    if (accQ.data) setName(accQ.data.name);
  }, [accQ.data]);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Label>Название УК</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ООО «Пример»" />
      </div>
      <Button onClick={() => mut.mutate({ name })} disabled={!name.trim() || mut.isPending}>
        Сохранить
      </Button>
    </div>
  );
}

function StepSource() {
  // Пока RC-webhook не принял ни одной брони - шаг нельзя закрыть. Poll'им state каждые 5с,
  // чтобы баннер сам позеленел, когда бронь прилетит (без ручного refresh).
  const stateQ = useQuery({
    queryKey: ['setup'],
    queryFn: fetchSetupState,
    refetchInterval: (q) => (q.state.data?.sourceReady ? false : 5000),
  });
  const ready = stateQ.data?.sourceReady ?? false;
  return (
    <div className="flex flex-col gap-3">
      <div
        className={cn(
          'flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
          ready ? 'border-green/40 bg-green/10 text-green' : 'border-amber/40 bg-amber/10 text-amber',
        )}
      >
        {ready ? (
          <>
            <CheckCircleIcon weight="fill" className="mt-0.5 size-4 shrink-0" />
            <span>Первая бронь принята - источник работает. Можно продолжать.</span>
          </>
        ) : (
          <>
            <PlugsIcon weight="bold" className="mt-0.5 size-4 shrink-0" />
            <span>
              Шаг закроется сам, когда придёт первая бронь - из RealtyCalendar, iCal или файла.
              Настройте любой источник ниже и дождитесь первого события.
            </span>
          </>
        )}
      </div>
      {/* Источники настраиваются прямо здесь. Раньше на этом месте стояли четыре плитки
          с кнопкой «Открыть /integrations»: мастер выкидывал человека на другую страницу
          и терял его - вернуться на нужный шаг он мог только через баннер. Карточки те же
          самые, что на «Интеграциях», а не копии: правка в одном месте видна в обоих. */}
      <p className="text-xs text-muted">
        Достаточно одного источника. Webhook и iCal работают постоянно, файл - разовый перенос.
      </p>
      <RealtyCalendarCard embedded />
      <IcalFeedsCard embedded />
      <BookingsImportCard embedded />
      <div className="rounded-md border border-line bg-panel2/40 p-3">
        <div className="text-sm font-semibold text-muted">Email-ящик</div>
        <div className="mt-1 text-xs text-muted">
          IMAP с разбором писем о бронях от площадок. Появится после MVP - настраивать пока нечего.
        </div>
      </div>
    </div>
  );
}

function StepProperties() {
  // Заведение объектов - прямо здесь. Раньше на этом месте были две кнопки-ссылки, и обе
  // уводили из мастера: «Импорт из URL» вообще на /bookings, где никакого импорта нет.
  return <PropertyOnboardingCard />;
}

function StepPropertyForm() {
  const navigate = useNavigate();
  const propsQ = useQuery({ queryKey: ['properties'], queryFn: fetchProperties });
  const first = propsQ.data?.[0];
  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs text-muted">
        Заполните <b>Wi-Fi, замок, домофон, экстренные</b> для первого объекта - без этих полей
        бот эскалирует гостя оператору (ADR-7).
      </div>
      {first ? (
        <Button onClick={() => navigate(`/properties/${first.id}`)}>
          Открыть анкету «{first.title}»
        </Button>
      ) : (
        <div className="text-sm text-muted">Объектов ещё нет - вернитесь на предыдущий шаг.</div>
      )}
    </div>
  );
}

function StepChatImport() {
  const qc = useQueryClient();
  const statusQ = useQuery({ queryKey: ['avito-status'], queryFn: fetchAvitoStatus });
  const conn = statusQ.data?.connection ?? null;
  const canImportAvito = !!conn && conn.tariffOk && !!conn.avitoUserId;

  const importHistoryMut = useMutation({
    mutationFn: importAvitoHistory,
    onSuccess: (r) => {
      toast.success(
        `Импортировано ${r.savedDocs} диалогов из ${r.fetchedChats} чатов (${r.fetchedMessages} сообщений)`,
      );
      void qc.invalidateQueries({ queryKey: ['avito-status'] });
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs text-muted">
        Из переписок бот учится вашим формулировкам и учитывает частые вопросы гостей.
        Два источника: файлы или Авито-чаты.
      </div>

      {/* Вариант 1: Авито - если подключён на прошлом шаге */}
      <div className="rounded-md border border-line bg-panel2/40 p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <StorefrontIcon weight="bold" className="size-4" /> Из Авито (мессенджер)
        </div>
        {canImportAvito ? (
          <>
            <div className="mb-2 text-xs text-muted">
              Тянем последние чаты по объявлениям (лимит Авито - ~100 чатов), собираем транскрипт
              «гость ↔ УК» и кладём в базу знаний. Чаты с parsing_allowed=false пропускаем.
            </div>
            <Button
              size="sm"
              onClick={() => importHistoryMut.mutate()}
              disabled={importHistoryMut.isPending}
            >
              {importHistoryMut.isPending ? 'Импортируем…' : 'Импортировать чаты из Авито'}
            </Button>
          </>
        ) : (
          <div className="text-xs text-muted">
            {!conn
              ? 'Авито не подключён - вернитесь на шаг «Авито» и добавьте ключи.'
              : !conn.tariffOk
                ? 'Тариф Авито не позволяет использовать Messenger API - импорт недоступен.'
                : 'Ждём резолв avito_user_id (переподключите Авито).'}
          </div>
        )}
      </div>

      <TgHistoryImportCard />

      <MaxHistoryImportCard />

      {/* Вариант 2: файлы - drag-drop в разработке */}
      <div className="rounded-md border border-dashed border-line bg-panel2/40 p-6 text-center text-xs text-muted">
        Файлы TXT / HTML-экспорт TG / JSON - drag-drop area, реализация после запуска KB_FACT_MINING.
      </div>
    </div>
  );
}

// ============================================================ StepAvito
function StepAvito() {
  const qc = useQueryClient();
  const statusQ = useQuery({ queryKey: ['avito-status'], queryFn: fetchAvitoStatus });
  const conn = statusQ.data?.connection ?? null;

  const [cid, setCid] = useState('');
  const [csec, setCsec] = useState('');

  // Путь B: кнопка появляется, только если стенд настроен на наше OAuth-приложение.
  const oauthQ = useQuery({ queryKey: ['avito-oauth-config'], queryFn: fetchAvitoOauthConfig });
  const oauthMut = useMutation({
    mutationFn: startAvitoOauth,
    onSuccess: (r) => window.location.assign(r.authorizeUrl),
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const connectMut = useMutation({
    mutationFn: () => connectAvito(cid.trim(), csec.trim()),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['avito-status'] });
      if (r.probe.tariffOk) {
        toast.success('Авито подключён, тариф ok');
      } else if (r.probe.error) {
        toast.error(`Подключено, но не работает: ${r.probe.error}`);
      }
      setCid('');
      setCsec('');
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  // Выход из состояния «панель говорит, что тарифа нет»: перепроверяет по сохранённым ключам,
  // без переподключения и без повторного ввода client_id/secret.
  const probeMut = useMutation({
    mutationFn: probeAvitoTariff,
    onSuccess: (r) => {
      if (r.tariffOk) toast.success('Тариф подтверждён: Messenger API доступен');
      else toast.error(r.error ?? 'Тариф всё ещё не подтверждён');
      void qc.invalidateQueries({ queryKey: ['avito-status'] });
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const importItemsMut = useMutation({
    mutationFn: importAvitoItems,
    onSuccess: (r) => {
      toast.success(
        `Импортировано: ${r.created} новых, ${r.updated} обновлено (всего с Авито ${r.fetched})`,
      );
      void qc.invalidateQueries({ queryKey: ['properties'] });
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  return (
    <div className="flex flex-col gap-4">
      {/* Что даёт интеграция */}
      <div className="rounded-md border border-line bg-panel2/40 p-3 text-sm">
        <div className="mb-2 font-semibold">Что даёт подключение Авито</div>
        <ul className="ml-4 list-disc space-y-1 text-xs text-muted">
          <li>
            <b>Импорт объявлений</b> - готовые черновики объектов (адрес, цена, ссылка), не надо заполнять руками.
          </li>
          <li>
            <b>Импорт истории чатов</b> - бот учится на ваших ответах гостям, находит частые вопросы и типовые формулировки.
          </li>
          <li>
            <b>Позже:</b> бот отвечает в мессенджере Авито наравне с Telegram и веб-чатом (ADR-12 в проработке).
          </li>
        </ul>
      </div>

      {/* Форма подключения / статус */}
      {conn ? (
        <div className="flex flex-col gap-3 rounded-md border border-line bg-panel/40 p-3">
          <div className="flex items-center gap-2 text-sm">
            <StatusChip tone={conn.tariffOk ? 'green' : conn.status === 'no_tariff' ? 'red' : 'amber'}>
              {conn.tariffOk ? 'подключено' : conn.status === 'no_tariff' ? 'нет тарифа' : conn.status}
            </StatusChip>
            <span className="text-muted">
              {conn.authKind === 'authorization_code' ? (
                <>через подтверждение на Авито</>
              ) : (
                <>
                  client_id: <code>{conn.clientId.slice(0, 8)}…</code>
                </>
              )}
              {conn.avitoUserId ? <> · avito_user_id: {conn.avitoUserId}</> : null}
            </span>
          </div>
          {conn.lastError ? (
            <div className="rounded-md border border-red/40 bg-red/10 p-2 text-xs text-red">
              {conn.lastError}
            </div>
          ) : null}
          {/* Импорт объявлений идёт через items:info и мессенджер-тарифа НЕ требует - сервер его
              и не проверяет. Гейт на tariffOk здесь был лишним: УК с валидным подключением,
              но без тарифа на Messenger, не мог импортировать объявления вообще ниоткуда. */}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => importItemsMut.mutate()} disabled={importItemsMut.isPending}>
              {importItemsMut.isPending ? 'Импортируем…' : 'Импортировать объявления → черновики'}
            </Button>
            {!conn.tariffOk ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => probeMut.mutate()}
                disabled={probeMut.isPending}
              >
                {probeMut.isPending ? 'Проверяем…' : 'Проверить тариф'}
              </Button>
            ) : null}
          </div>
          {!conn.tariffOk ? (
            <div className="text-xs text-muted">
              Переписка с гостями в Авито недоступна: для неё нужен тариф с Messenger API. На импорт
              объявлений это не влияет. Если тариф уже подключён, нажмите «Проверить тариф».
            </div>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-3 rounded-md border border-line bg-panel/40 p-3">
          {oauthQ.data?.available ? (
            <div className="flex flex-col gap-2 border-b border-line pb-3">
              <div className="text-xs text-muted">
                Быстрый путь: подтвердите доступ на Авито - ключи вводить не нужно.
              </div>
              <Button onClick={() => oauthMut.mutate()} disabled={oauthMut.isPending}>
                {oauthMut.isPending ? 'Открываем Авито…' : 'Подключить через Авито'}
              </Button>
              <div className="text-[11px] text-muted">
                Вернётесь сюда после подтверждения. Или подключите ключами из ЛК - ниже.
              </div>
            </div>
          ) : null}
          <div className="text-xs text-muted">
            Возьмите ключи в ЛК Авито: «Для профессионалов» → «API». В форме заполните любое название
            и «Redirect URL» может остаться пустым - для нашей интеграции он не используется.
          </div>
          <a
            href="https://www.avito.ru/professionals/api"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-accent-fg hover:underline"
          >
            Открыть кабинет Авито <ArrowSquareOutIcon weight="bold" className="size-3" />
          </a>
          <div className="flex flex-col gap-1">
            <Label>client_id</Label>
            <Input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="NJxdYvREgjHip6lJ-ahQ" />
          </div>
          <div className="flex flex-col gap-1">
            <Label>client_secret</Label>
            <Input
              type="password"
              value={csec}
              onChange={(e) => setCsec(e.target.value)}
              placeholder="секрет из ЛК Авито"
            />
          </div>
          <Button
            onClick={() => connectMut.mutate()}
            disabled={!cid.trim() || !csec.trim() || connectMut.isPending}
          >
            {connectMut.isPending ? 'Проверяем…' : 'Подключить и проверить'}
          </Button>
        </div>
      )}

      <div className="text-xs text-muted">
        Если у вас нет тарифа с API - шаг можно пропустить. Объекты и переписки можно добавить вручную позже.
      </div>
    </div>
  );
}

function StepPersona() {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: patchPlaybook,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['playbook'] });
      toast.success('Персона сохранена');
    },
  });
  const [tone, setTone] = useState('нейтральный');
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Label>Тон общения</Label>
        <select
          value={tone}
          onChange={(e) => setTone(e.target.value)}
          className="h-10 rounded border border-line-control bg-input px-2 text-sm"
        >
          <option value="дружелюбный">дружелюбный</option>
          <option value="нейтральный">нейтральный</option>
          <option value="формальный">формальный</option>
        </select>
      </div>
      <div className="text-xs text-muted">
        Агент представляется как ассистент УК (ADR-15). Полный редактор персоны и holder-текста -
        в /agent.
      </div>
      <Button onClick={() => mut.mutate({ tone })} disabled={mut.isPending}>
        Сохранить тон
      </Button>
    </div>
  );
}

function StepDemoChat() {
  // Настоящая проверка, а не инструкция «откройте гостевой чат по ссылке из брони»:
  // тот же конвейер, но без внешних эффектов - см. apps/api/src/common/sandbox.ts.
  return <SandboxChat />;
}

function StepSwitches() {
  // Не «рубильники», а объяснение, как пользоваться: draft-режим, живой пример карточки
  // подтверждения и напоминание о переключении, когда агент себя показал.
  return <DraftModeExplainer />;
}

// React импортирован в начале файла - inline-хуки step-компонентов используют его напрямую.
void React;
